import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type {
    AccessibilityPrefs,
    IncreaseContrastPref,
    ReduceMotionPref,
    TextSizePref,
} from '@/types/models';

const TEXT_SIZE_VALUES: readonly TextSizePref[] = ['default', 'large', 'extra-large'];
const TRISTATE_VALUES: readonly ReduceMotionPref[] = ['auto', 'always', 'never'];

const DEFAULTS = {
    reduceMotion: 'auto' as ReduceMotionPref,
    increaseContrast: 'auto' as IncreaseContrastPref,
    underlineLinks: false,
    textSize: 'default' as TextSizePref,
};

function isTristate(value: unknown): value is ReduceMotionPref {
    return value === 'auto' || value === 'always' || value === 'never';
}

function isTextSize(value: unknown): value is TextSizePref {
    return value === 'default' || value === 'large' || value === 'extra-large';
}

function tristateToOverride(value: ReduceMotionPref | IncreaseContrastPref): boolean | null {
    if (value === 'always') {
        return true;
    }
    if (value === 'never') {
        return false;
    }

    return null;
}

export const useA11yStore = defineStore('a11y', () => {
    const reduceMotion = ref<ReduceMotionPref>(DEFAULTS.reduceMotion);
    const increaseContrast = ref<IncreaseContrastPref>(DEFAULTS.increaseContrast);
    const underlineLinks = ref<boolean>(DEFAULTS.underlineLinks);
    const textSize = ref<TextSizePref>(DEFAULTS.textSize);

    function hydrate(prefs: Partial<AccessibilityPrefs> | null | undefined): boolean {
        if (!prefs || typeof prefs !== 'object') {
            return false;
        }
        let touched = false;
        if (isTristate(prefs.reduce_motion)) {
            reduceMotion.value = prefs.reduce_motion;
            touched = true;
        }
        if (isTristate(prefs.increase_contrast)) {
            increaseContrast.value = prefs.increase_contrast;
            touched = true;
        }
        if (typeof prefs.underline_links === 'boolean') {
            underlineLinks.value = prefs.underline_links;
            touched = true;
        }
        if (isTextSize(prefs.text_size)) {
            textSize.value = prefs.text_size;
            touched = true;
        }

        return touched;
    }

    const overrides = computed(() => ({
        reducedMotion: tristateToOverride(reduceMotion.value),
        highContrast: tristateToOverride(increaseContrast.value),
    }));

    function applyToDocument(): void {
        if (typeof document === 'undefined') {
            return;
        }
        const html = document.documentElement;

        // Reduce motion override (forces reduce on/off, overriding OS pref)
        html.classList.toggle('a11y-force-reduce-motion', reduceMotion.value === 'always');
        html.classList.toggle('a11y-allow-motion', reduceMotion.value === 'never');

        // High contrast override
        html.classList.toggle('a11y-force-high-contrast', increaseContrast.value === 'always');
        html.classList.toggle('a11y-no-high-contrast', increaseContrast.value === 'never');

        // Underline all links
        html.classList.toggle('a11y-underline-links', underlineLinks.value === true);

        // Text size scale via CSS variable + data attribute
        if (textSize.value === 'default') {
            html.removeAttribute('data-text-size');
        } else {
            html.setAttribute('data-text-size', textSize.value);
        }
    }

    function applyAllReactive(): void {
        applyToDocument();
        watch([reduceMotion, increaseContrast, underlineLinks, textSize], () => {
            applyToDocument();
        });
    }

    return {
        reduceMotion,
        increaseContrast,
        underlineLinks,
        textSize,
        overrides,
        hydrate,
        applyToDocument,
        applyAllReactive,
    };
});

export const A11Y_TRISTATE_VALUES = TRISTATE_VALUES;
export const A11Y_TEXT_SIZE_VALUES = TEXT_SIZE_VALUES;
